import { useCallback } from 'react';

/**
 * Keyboard handler for the CommandBar palette.
 *
 * Lifted out of the component so its 4-arrow / Enter / Escape branches
 * don't inflate the parent's cyclomatic complexity. Pure logic — no state.
 *
 * Returns `handleKeyDown(e)` ready to wire into an input's `onKeyDown`.
 *
 *   - `Escape`  → onClose()
 *   - `↓`       → bumps selectedIndex, bounded by results + aiResult slot
 *   - `↑`       → decrements selectedIndex (min 0)
 *   - `Enter`   → activates selected row (AI card if at index 0 when present)
 */
export function useCommandBarKeyboard({
  results,
  aiResult,
  selectedIndex,
  setSelectedIndex,
  executeAiResult,
  onClose,
}) {
  return useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const max = results.length - 1 + (aiResult ? 1 : 0);
      setSelectedIndex((i) => Math.min(i + 1, max));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex === 0 && aiResult) {
        executeAiResult();
        return;
      }
      const idx = aiResult ? selectedIndex - 1 : selectedIndex;
      if (results[idx]?.action) {
        results[idx].action();
        onClose();
      }
    }
  }, [results, aiResult, selectedIndex, setSelectedIndex, executeAiResult, onClose]);
}
